// LusideaWebsite.js - Optimized landing page with SEO enhancements
import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet'; // Using regular react-helmet instead of react-helmet-async
import {
  Check, Compass, Lightbulb, Target, TrendingUp, ArrowRight, 
  ArrowUpRight, Award, CheckCircle2, MessageSquare, 
  BrainCircuit, Settings, Calendar, Clock, Zap,
  BarChart3, Frame, RefreshCw, Layers, Star, Eye, Share2, 
  Bookmark, ChevronRight, X
} from 'lucide-react';

// More efficient imports with lazy loading for performance
import HeaderNav from './HeaderNav';
import Button from './Button';
// Lazy load non-critical components to improve initial load time
const Contact = lazy(() => import('./Contact'));
const FooterLan = lazy(() => import('./FooterLan'));

// --- Optimized color palette with semantic variable names ---
const COLORS = {
  primary: '#ce2223',       // Lusidea Red
  primaryDark: '#b01a1b',
  primaryLight: '#f8e7e7',  
  primaryLighter: '#fef2f2',
  secondary: '#fdb52a',     // Accent Yellow/Gold
  secondaryDark: '#e09c18',
  secondaryLight: '#fff8eb', 
  secondaryLighter: '#fffbf0',
  black: '#111827',         
  darkGray: '#374151',      
  mediumGray: '#6b7280',    
  lightGray: '#e5e7eb',     
  offWhite: '#f9fafb',      
  white: '#ffffff',
  success: '#10b981',
  successLight: '#ecfdf5'
};

// --- Improved spacing system with better descriptive names ---
const SPACING = {
  xs: 'px-3 py-1.5',
  sm: 'px-4 py-2.5',
  md: 'px-6 py-4',
  lg: 'px-8 py-6',
  xl: 'px-12 py-8',
  '2xl': 'px-16 py-12',
  section: 'py-14 md:py-20 lg:py-24',
  sectionMedium: 'py-10 md:py-16 lg:py-20', 
  sectionCompact: 'py-8 md:py-12 lg:py-16',
  container: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'
};

// --- Enhanced SEO Component with Schema.org structured data ---
const SEO = React.memo(({ 
  title, 
  description, 
  keywords, 
  image, 
  url, 
  structuredData, 
  canonical,
  author = "Lusidea",
  lang = "en",
  publishedDate,
  modifiedDate
}) => {
  // Default meta values for SEO with improved keyword targeting
  const metaTitle = title || 'Lusidea - Innovation Consulting & Strategy | Future-Ready Solutions';
  const metaDescription = description || 'Lusidea partners with organizations to build robust innovation capabilities. We transform centuries of knowledge into practical, future-driven solutions tailored to your unique business needs.';
  const metaKeywords = keywords || 'innovation management, future thinking, design thinking, innovation strategy, innovation consulting, digital transformation, business innovation';
  const metaImage = image || '/img/lusidea-og-image.jpg';
  const metaUrl = url || 'https://www.lusidea.com';
  const metaCanonical = canonical || metaUrl;
  
  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="author" content={author} />
      
      {/* Responsive meta tag with improved viewport settings */}
      <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no, viewport-fit=cover" />
      
      {/* Color scheme and theme color for browser UI */}
      <meta name="theme-color" content={COLORS.primary} />
      <meta name="color-scheme" content="light" />
      
      {/* Open Graph tags with enhanced properties */}
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:image:alt" content={metaTitle} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={metaUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Lusidea" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card tags with enhanced properties */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:site" content="@lusidea" />
      <meta name="twitter:creator" content="@lusidea" />
      
      {/* Published and modified dates for SEO */}
      {publishedDate && <meta name="article:published_time" content={publishedDate} />}
      {modifiedDate && <meta name="article:modified_time" content={modifiedDate} />}
      
      {/* Canonical URL to prevent duplicate content issues */}
      <link rel="canonical" href={metaCanonical} />
      
      {/* DNS prefetch and preconnect for external resources */}
      <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
      <link rel="dns-prefetch" href="https://fonts.gstatic.com" />
      <link rel="preconnect" href="https://fonts.googleapis.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* Preload critical assets */}
      <link rel="preload" as="image" href="/img/lusidea-logo.png" />
      
      {/* Structured data for rich snippets */}
      {Array.isArray(structuredData) ? (
        structuredData.map((data, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(data)}
          </script>
        ))
      ) : structuredData ? (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      ) : null}
    </Helmet>
  );
});

// Properly set displayName for React.memo component
SEO.displayName = 'SEO';

// --- Optimized Toast Notification Component with Accessibility Improvements ---
const Toast = React.memo(({ message, type = 'success', onClose }) => {
  // Auto-close toast after delay with cleanup
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const bgColor = type === 'success' ? 'bg-successLight' : 'bg-primaryLight';
  const textColor = type === 'success' ? 'text-success' : 'text-primary';
  const icon = type === 'success' ? <CheckCircle2 size={18} /> : <ChevronRight size={18} />;
  
  return (
    <div 
      className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${bgColor} border border-lightGray/20 max-w-sm flex items-center gap-3`} 
      role="alert"
      aria-live="assertive"
    >
      <span className={textColor} aria-hidden="true">{icon}</span>
      <p className="text-darkGray font-medium">{message}</p>
      <button 
        onClick={onClose} 
        className="ml-auto text-mediumGray hover:text-primary transition-colors"
        aria-label="Close notification"
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
});

Toast.displayName = 'Toast';

// --- Custom hook for viewport detection to optimize re-renders ---
const useViewport = () => {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);
  
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return { width, isMobile: width < 768, isTablet: width >= 768 && width < 1024, isDesktop: width >= 1024 };
};

// --- Optimized hook for scroll detection ---
const useScrollPosition = (threshold = 500) => {
  const [isScrolled, setIsScrolled] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > threshold);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [threshold]);
  
  return isScrolled;
};

// --- Main App Component with SEO and Performance Optimizations ---
const LusideaWebsite = () => {
  // Use custom hooks for better performance
  const showScrollTop = useScrollPosition(500);
  const [toast, setToast] = useState(null);

  // Memoized scrollToTop function
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Enhanced structured data with BreadcrumbList for better SEO
  const structuredData = useMemo(() => {
    const organizationData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Lusidea",
      "url": "https://www.lusidea.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.lusidea.com/img/lusidea-logo.png",
        "width": "180",
        "height": "60"
      },
      "description": "Lusidea partners with you to build robust innovation capabilities. We transform centuries of knowledge into practical, future-driven solutions tailored to your needs.",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+1-123-456-7890",
        "contactType": "customer service",
        "availableLanguage": ["English"]
      },
      "sameAs": [
        "https://www.linkedin.com/company/lusidea",
        "https://twitter.com/lusidea",
        "https://www.facebook.com/lusidea"
      ],
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "123 Innovation Ave",
        "addressLocality": "San Francisco",
        "addressRegion": "CA",
        "postalCode": "94103",
        "addressCountry": "US"
      },
      "founders": [{
        "@type": "Person",
        "name": "Lusidea Founder",
        "jobTitle": "CEO and Founder"
      }]
    };
    
    const websiteData = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "url": "https://www.lusidea.com",
      "name": "Lusidea - Innovation Consulting & Strategy",
      "description": "Lusidea partners with organizations to build robust innovation capabilities through expert consulting, innovation management, and future thinking methodologies.",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://www.lusidea.com/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    };
    
    const breadcrumbData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://www.lusidea.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Services",
          "item": "https://www.lusidea.com/#services"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "Blog",
          "item": "https://www.lusidea.com/blog"
        }
      ]
    };
    
    return [organizationData, websiteData, breadcrumbData];
  }, []);

  return (
    <div className="font-sans antialiased text-darkGray bg-white overflow-x-hidden min-h-screen">
      {/* Enhanced SEO with multiple structured data objects and additional metadata */}
      <SEO 
        title="Lusidea - Turn Ideas Into Measurable Impact | Innovation Consulting"
        description="Lusidea partners with organizations to build robust innovation capabilities through expert consulting, innovation management, and future thinking methodologies. Discover our proven approach to innovation success."
        keywords="innovation management, future thinking, innovation strategy, innovation consulting, design thinking, business transformation, idea management, digital innovation, corporate innovation, innovation programs"
        structuredData={structuredData}
        author="Lusidea Innovation Consulting"
        canonical="https://www.lusidea.com/"
        publishedDate="2023-01-01T08:00:00+00:00"
        modifiedDate={new Date().toISOString()}
      />
      
      {/* Simplified and reduced animations for better performance */}
      <style>
        {`
          /* Core Web Vitals optimizations for Cumulative Layout Shift */
          img, picture {
            max-width: 100%;
            display: block;
          }
          
          /* Improved focus styles for accessibility */
          :focus-visible {
            outline: 2px solid ${COLORS.primary};
            outline-offset: 2px;
          }
        `}
      </style>
      
      <HeaderNav />
      
      {/* Toast notification system with improved accessibility */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}
      
      <main id="main-content" tabIndex="-1" className="space-y-0">
        <Hero />
        <div className="py-6 bg-gradient-to-b from-white to-offWhite">
          <Services />
        </div>
        <Approach />
        <div className="py-6 bg-gradient-to-b from-offWhite to-white">
          <Showcase />
        </div>
        
        {/* Optimized Blog Section */}
        <SimplifiedBlog setToast={setToast} />
        
        {/* Suspense for lazy-loaded components with improved fallback UI */}
        <Suspense fallback={
          <div className="py-16 text-center">
            <div className="inline-block p-4 bg-lightGray/40 rounded-lg">
              <span className="sr-only">Loading contact section...</span>
              <div className="w-32 h-6 bg-lightGray rounded-md"></div>
            </div>
          </div>
        }>
          <Contact />
        </Suspense>
      </main>
      
      <Suspense fallback={
        <div className="py-8 text-center">
          <div className="inline-block p-4 bg-lightGray/40 rounded-lg">
            <span className="sr-only">Loading footer...</span>
            <div className="w-32 h-6 bg-lightGray rounded-md"></div>
          </div>
        </div>
      }>
        <FooterLan />
      </Suspense>
      
      {/* Back to top button with improved accessibility and performance */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-primary text-white shadow-lg flex items-center justify-center hover:bg-primaryDark transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
          aria-label="Scroll to top"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 15l-6-6-6 6" />
          </svg>
        </button>
      )}
    </div>
  );
};

// --- Hero Section with Responsiveness Improvements ---
const Hero = () => {
  return (
    <section id="hero" className="relative pt-24 sm:pt-28 lg:pt-32 pb-16 md:pb-20 lg:pb-24 overflow-hidden">
      {/* Background gradient with reduced visual complexity */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primaryLight via-white to-secondaryLight opacity-70" aria-hidden="true"></div>
      
      <div className={`${SPACING.container} z-10 relative`}>
        <div className="flex flex-col lg:flex-row lg:gap-12 items-center">
          {/* Text Content Area - Improved responsive structure */}
          <div className="lg:w-7/12 text-center lg:text-left">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-black leading-tight mb-6">
              Turn <span className="text-primary">Ideas</span> Into <br className="hidden md:inline" /> Measurable <span className="text-primary">Impact</span>
            </h1>

            <p className="text-lg sm:text-xl text-darkGray mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
              Lusidea partners with you to build robust innovation capabilities. We transform centuries of knowledge into practical, future-driven solutions tailored to your needs.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button 
                href="#contact" 
                variant="primary" 
                size="lg" 
                icon={ArrowRight} 
                className="w-full sm:w-auto shadow-md"
              >
                Schedule a Consultation
              </Button>
              <Button 
                href="#services" 
                variant="outlineGray" 
                size="lg" 
                icon={Compass} 
                className="w-full sm:w-auto mt-3 sm:mt-0"
              >
                Explore Our Services
              </Button>
            </div>

            {/* Key Principles section - Improved for mobile */}
            <div className="mt-10 pt-6 border-t border-lightGray/60">
              <h2 className="sr-only">Our Key Principles</h2>
              <ul className="flex flex-col sm:flex-row gap-4 sm:gap-8 justify-center lg:justify-start">
                <li className="flex items-center justify-center sm:justify-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primaryLight flex items-center justify-center shadow-sm" aria-hidden="true">
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium text-base text-black">Human-Centered</span>
                </li>
                <li className="flex items-center justify-center sm:justify-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primaryLight flex items-center justify-center shadow-sm" aria-hidden="true">
                    <BrainCircuit className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium text-base text-black">Future-Driven</span>
                </li>
                <li className="flex items-center justify-center sm:justify-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primaryLight flex items-center justify-center shadow-sm" aria-hidden="true">
                    <Settings className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium text-base text-black">End-to-End Process</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Hero Image Area - Enhanced for mobile and performance */}
          <div className="lg:w-5/12 mt-10 lg:mt-0 w-full max-w-md mx-auto lg:max-w-none lg:mx-0">
            <div className="relative z-10">
              {/* Main image with optimized loading */}
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="/img/innomanagement.png"
                  alt="Team collaborating on innovation strategy with a digital whiteboard"
                  className="w-full h-full object-cover"
                  loading="eager" 
                  width="600"
                  height="400"
                  fetchPriority="high"
                  decoding="async"
                />
              </div>
              
              {/* Enhanced floating card element - responsive and mobile friendly */}
              <div className="absolute -bottom-6 -left-6 sm:-bottom-8 sm:-left-8 bg-white rounded-lg shadow-lg p-4 border border-lightGray/40">
                <div className="flex items-center gap-3">
                  <TrendingUp size={24} className="text-primary" aria-hidden="true" />
                  <div>
                    <div className="text-sm text-mediumGray font-medium">Accelerating Growth</div>
                    <div className="text-lg font-bold text-black">Ideas to Reality</div>
                  </div>
                </div>
              </div>
              
              {/* Enhanced second floating element - mobile optimized */}
              <div className="absolute -top-4 -right-4 sm:-top-6 sm:-right-6 bg-secondary/90 text-black rounded-lg shadow-lg py-2 px-4">
                <div className="font-bold flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-black" aria-hidden="true" />
                  <span>90% Success Rate</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Services Section with Responsive Enhancements ---
const Services = () => {
  // Memoized services data
  const services = useMemo(() => [
    { 
      icon: Settings, 
      title: "Innovation Management", 
      description: "We structure and optimize your innovation engine, aligning processes with strategy for greater efficiency and customer appeal. Our comprehensive approach ensures your innovation activities produce measurable results aligned with business objectives.", 
      linkText: "Optimize Your Innovation Process", 
      link: "/approach",
      image: "/img/innomanagement.png",
      color: "primary",
      features: [
        "Strategic innovation alignment",
        "Innovation portfolio management",
        "Cross-functional collaboration frameworks",
        "Innovation metrics and measurement",
        "Knowledge management systems",
        "Organizational innovation culture"
      ]
    },
    { 
      icon: Compass, 
      title: "Future Thinking", 
      description: "Gain foresight into market shifts and emerging opportunities. We help you anticipate change through strategic foresight, scenario planning, and trend analysis to position your organization ahead of disruption and create sustainable competitive advantage.", 
      linkText: "Anticipate Future Trends", 
      link: "/approach",
      image: "/img/futurethinking.png",
      color: "secondary",
      features: [
        "Trend scanning and analysis",
        "Scenario development and planning",
        "Future business model exploration",
        "Emerging technology assessment",
        "Consumer behavior forecasting",
        "Disruption risk management"
      ]
    }
  ], []);

  return (
    <section id="services" className={`${SPACING.section} relative overflow-hidden`}>
      {/* Simplified background for better performance */}
      <div 
        className="absolute inset-0 -z-10 opacity-20" 
        style={{
          backgroundImage: 'radial-gradient(#e5e7eb 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }} 
        aria-hidden="true"
      />
      
      <div className={SPACING.container}>
        {/* Enhanced section header with improved responsive sizing */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-block rounded-full bg-primaryLight px-4 py-1.5 mb-3">
            <p className="text-sm font-semibold text-primary uppercase tracking-wide">
              Specialized Innovation Services
            </p>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-4">
            How We Help You <span className="text-primary">Innovate</span>
          </h2>
          <p className="text-base sm:text-lg text-darkGray max-w-2xl mx-auto leading-relaxed">
            Our focused expertise in Innovation Management and Future Thinking provides the foundation for sustainable growth and transformation.
          </p>
        </div>

        {/* Redesigned service cards with improved mobile design */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          {services.map((service, index) => {
            const serviceColor = service.color === 'primary' ? 
              { light: 'bg-primaryLight', lighter: 'bg-primaryLighter', main: 'bg-primary text-white', text: 'text-primary' } : 
              { light: 'bg-secondaryLight', lighter: 'bg-secondaryLighter', main: 'bg-secondary text-black', text: 'text-secondaryDark' };
            
            return (
              <article 
                key={index} 
                className="bg-white rounded-xl overflow-hidden shadow-lg border border-lightGray hover:shadow-xl transition-all duration-300 flex flex-col h-full"
              >
                {/* Image Section with optimized loading */}
                <div className="relative h-52 sm:h-56 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={`${service.title} - ${service.description.substring(0, 60)}...`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="600"
                    height="350"
                    decoding="async"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col justify-end p-6`}>
                    <div className="flex items-center mb-2">
                      <div className={`w-12 h-12 rounded-lg ${serviceColor.main} flex items-center justify-center mr-3 shadow-md`} aria-hidden="true">
                        {React.createElement(service.icon, { size: 24 })}
                      </div>
                      <h3 className="text-xl sm:text-2xl font-bold text-white">{service.title}</h3>
                    </div>
                  </div>
                </div>
                
                {/* Content Section - Improved layout for mobile */}
                <div className="p-6 flex flex-col flex-grow">
                  <p className="text-darkGray mb-6 text-base sm:text-lg leading-relaxed">{service.description}</p>
                  
                  {/* Features with improved list for mobile */}
                  <div className="mb-6 flex-grow">
                    <h4 className="flex items-center text-lg font-semibold text-black mb-4">
                      <CheckCircle2 className={`w-5 h-5 ${serviceColor.text} mr-2`} aria-hidden="true" />
                      Key Capabilities:
                    </h4>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-start">
                          <div className={`w-5 h-5 rounded-full ${serviceColor.light} flex-shrink-0 flex items-center justify-center mt-0.5 mr-2`} aria-hidden="true">
                            <Check size={12} className={serviceColor.text} />
                          </div>
                          <span className="text-sm sm:text-base text-darkGray">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* CTA Button with improved accessibility */}
                  <a
                    href={service.link}
                    className={`inline-flex items-center justify-center px-5 py-2.5 font-semibold rounded-lg ${service.color === 'primary' ? 'bg-primary text-white' : 'bg-secondary text-black'} hover:shadow-md transition-all duration-300 mt-auto self-start focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2`}
                    aria-label={`Learn more about ${service.title}`}
                  >
                    {service.linkText} <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
                  </a>
                </div>
              </article>
            );
          })}
        </div>
        
        {/* Additional Services Banner - Mobile optimized */}
        <div className="mt-12 md:mt-16">
          <div className="bg-gradient-to-r from-primaryLight via-white to-secondaryLight rounded-xl p-6 sm:p-8 md:p-10 text-center shadow-lg border border-lightGray/30">
            <h3 className="text-xl sm:text-2xl font-bold text-black mb-4">Looking for a custom solution?</h3>
            <p className="text-base md:text-lg text-darkGray mb-6 max-w-2xl mx-auto">
              We offer tailored innovation programs designed to meet your organization's specific needs and challenges. Let's discuss how we can help you innovate more effectively.
            </p>
            <Button href="/contact" variant="primary" size="lg" icon={MessageSquare} className="shadow-md">
              Discuss Your Innovation Needs
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Approach Section with Mobile Optimizations ---
const Approach = () => {
  // Memoized steps data
  const steps = useMemo(() => [
    { 
      number: "01", 
      title: "FRAME", 
      description: "Define innovation challenges, map the opportunity landscape, and align with strategic objectives to set the foundation for success.", 
      icon: Frame,
      color: "primary",
      details: [
        "Problem definition & opportunity mapping",
        "Strategic alignment with business goals",
        "Current-state capability assessment",
        "Innovation ecosystem analysis"
      ]
    },
    { 
      number: "02", 
      title: "SCALE", 
      description: "Develop, test, and expand innovation initiatives through structured processes that enable rapid experimentation and validation.", 
      icon: BarChart3,
      color: "secondary",
      details: [
        "Concept development & prototyping",
        "Rapid experimentation frameworks",
        "Validation methodologies",
        "Resource optimization strategies"
      ]
    },
    { 
      number: "03", 
      title: "IMPROVE", 
      description: "Continuously measure, learn, and refine your innovation approach to increase success rates and maximize business impact.", 
      icon: RefreshCw,
      color: "primary",
      details: [
        "Innovation metrics & measurement systems",
        "Continuous learning frameworks",
        "Process optimization techniques",
        "Knowledge management & transfer"
      ]
    }
  ], []);

  // Use custom viewport hook for responsive behaviors
  const { isDesktop } = useViewport();

  return (
    <section id="approach" className="py-16 md:py-20 lg:py-24 bg-gradient-to-br from-offWhite to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Enhanced section header with proper responsive sizing */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-block rounded-full bg-primaryLight px-4 py-1.5 mb-3">
            <p className="text-sm font-semibold text-primary uppercase tracking-wide">
              Our Methodology
            </p>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-4">
            Our <span className="text-primary">Proven</span> Path to Innovation Success
          </h2>
          <p className="text-base sm:text-lg text-darkGray max-w-2xl mx-auto leading-relaxed">
            We follow a structured yet flexible process, ensuring every step creates value towards your innovation goals.
          </p>
        </div>

        {/* Methodology visualization - improved responsive approach */}
        {!isDesktop ? (
          /* Mobile/Tablet View - Simplified for performance */
          <div className="relative max-w-xl mx-auto px-4">
            <ol className="space-y-12 relative">
              {steps.map((step, index) => {
                const colorClasses = {
                  primary: "bg-primary text-white border-primary",
                  secondary: "bg-secondary text-black border-secondary"
                };
                
                // Previous and next step indicators
                const isFirst = index === 0;
                
                return (
                  <li 
                    key={index} 
                    className="relative" 
                    id={`step-${index + 1}`}
                  >
                    {/* Connection line */}
                    {!isFirst && (
                      <div className="absolute left-8 -top-12 h-12 w-1 bg-lightGray/60" aria-hidden="true"></div>
                    )}
                    
                    {/* Step container */}
                    <article className="bg-white rounded-xl shadow-lg p-6 border border-lightGray hover:shadow-xl transition-all duration-300">
                      <div className="flex flex-col sm:flex-row gap-5">
                        {/* Left side - number and icon */}
                        <div className="flex flex-col items-center sm:items-start">
                          <div 
                            className={`w-16 h-16 rounded-xl ${colorClasses[step.color]} flex items-center justify-center text-lg font-bold mb-3 shadow-md`} 
                            aria-label={`Step ${step.number}`}
                          >
                            {step.number}
                          </div>
                          {React.createElement(step.icon, { 
                            size: 28, 
                            className: step.color === 'primary' ? 'text-primary' : 'text-secondaryDark', 
                            'aria-hidden': 'true' 
                          })}
                        </div>
                        
                        {/* Right side - content */}
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-black mb-3 text-center sm:text-left">{step.title}</h3>
                          <p className="text-base text-darkGray mb-4">{step.description}</p>
                          
                          {/* Details list with proper semantics */}
                          <ul className="space-y-2">
                            {step.details.map((detail, i) => (
                              <li key={i} className="flex items-start">
                                <Check 
                                  size={16} 
                                  className={step.color === 'primary' ? 'text-primary' : 'text-secondaryDark'} 
                                  aria-hidden="true" 
                                />
                                <span className="text-sm ml-2">{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </article>
                  </li>
                );
              })}
            </ol>
          </div>
        ) : (
          /* Desktop View - Simplified horizontal process flow */
          <div className="relative mt-8 mb-16">
            <ol className="grid grid-cols-3 gap-6">
              {steps.map((step, index) => {
                const isLast = index === steps.length - 1;
                const stepColorClass = step.color === 'primary' ? 'text-primary' : 'text-secondaryDark';
                const bgColorClass = step.color === 'primary' ? 'bg-primaryLight' : 'bg-secondaryLight';
                
                return (
                  <li 
                    key={index} 
                    className="relative"
                  >
                    {/* Connecting lines */}
                    {!isLast && (
                      <div className="absolute top-16 left-full w-6 h-1 bg-lightGray z-0" aria-hidden="true"></div>
                    )}
                    
                    {/* Main step container */}
                    <article className="bg-white rounded-xl shadow-lg p-6 border border-lightGray hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                      {/* Step number and title area */}
                      <div className="flex items-center mb-5">
                        <div 
                          className={`flex-shrink-0 w-14 h-14 rounded-lg ${step.color === 'primary' ? 'bg-primary text-white' : 'bg-secondary text-black'} flex items-center justify-center text-lg font-bold shadow-md`} 
                          aria-label={`Step ${step.number}`}
                        >
                          {step.number}
                        </div>
                        <h3 className="text-xl font-bold text-black ml-4">{step.title}</h3>
                      </div>
                      
                      {/* Icon */}
                      <div className={`w-12 h-12 rounded-full ${bgColorClass} flex items-center justify-center mb-4`} aria-hidden="true">
                        {React.createElement(step.icon, { size: 24, className: stepColorClass })}
                      </div>
                      
                      {/* Description */}
                      <p className="text-base text-darkGray mb-5 flex-grow">{step.description}</p>
                      
                      {/* Details with simplified list */}
                      <ul className="space-y-2 mt-auto">
                        {step.details.map((detail, i) => (
                          <li key={i} className="flex items-start">
                            <div className={`w-5 h-5 rounded-full ${bgColorClass} flex-shrink-0 flex items-center justify-center mt-0.5 mr-2`} aria-hidden="true">
                              <Check size={12} className={stepColorClass} />
                            </div>
                            <span className="text-sm text-darkGray">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </article>
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        {/* ISO Badge - Mobile optimized */}
        <div className="mt-10 flex justify-center" id="learn">
          <div 
            className="inline-flex items-center gap-3 bg-white px-5 py-3 rounded-full shadow-lg border border-lightGray/60"
            itemScope
            itemType="https://schema.org/Organization"  
          >
            <meta itemProp="name" content="Lusidea" />
            <Award size={20} className="text-secondary" aria-hidden="true" />
            <span className="font-medium text-base sm:text-lg">
              Aligned with <span className="font-bold text-black" itemProp="award">ISO 56000</span> Innovation Management Standards
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Showcase Section with Mobile Optimization ---
const Showcase = () => {
  // Memoized tools data
  const tools = useMemo(() => [
    { 
      icon: Zap, 
      title: "Lusidea Innovation Hub", 
      description: "Free demo no-signup access to our with 20+ tools to help you with innovation processes.", 
      link: "https://portal.lusidea.com/", 
      cta: "Explore Now" 
    },
    { 
      icon: Layers, 
      title: "Innovation Library", 
      description: "Centralized hub for curated business insights aligned with your strategic purpose.", 
      link: "/blog?category=Knowledge", 
      cta: "Explore Insights" 
    },
    { 
      icon: Lightbulb, 
      title: "Innovation Canvas", 
      description: "Transform your innovation ideas into structured strategies.", 
      link: "https://lusidea.com/tools/InnovationCanvasTool", 
      cta: "Evaluate Your Innovation Strategy" 
    },
    { 
      icon: Target, 
      title: "Innovation Health", 
      description: "Evaluate your organization's current innovation capabilities and health level.", 
      link: "https://lusidea.com/tools/InnovationHealthCheck", 
      cta: "Assess Now" 
    },
    { 
      icon: BrainCircuit, 
      title: "Future Signals Generator", 
      description: "Generate insights to anticipate market shifts and adapt proactively.", 
      link: "https://lusidea.com/tools/futuresignalscanner", 
      cta: "Get Signals" 
    },
    { 
      icon: CheckCircle2, 
      title: "Customer Journey Mapping", 
      description: "Quick understanding of your potential customers.", 
      link: "https://lusidea.com/tools/customerJourneyMapper", 
      cta: "Understand Persona Journey" 
    },
  ], []);

  return (
    <section id="showcase" className="py-16 md:py-20 lg:py-24 relative">
      {/* Simplified background decoration for better performance */}
      <div className="absolute top-0 right-0 -z-10 w-full h-full overflow-hidden" aria-hidden="true">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primaryLight rounded-full blur-3xl opacity-30"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-secondaryLight rounded-full blur-3xl opacity-30"></div>
      </div>
      
      <div className={SPACING.container}>
        {/* Section header with proper responsive sizing */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-block rounded-full bg-secondaryLight px-4 py-1.5 mb-3">
            <p className="text-sm font-semibold text-secondaryDark uppercase tracking-wide">
              Our Innovation Toolkit
            </p>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-4">
            Tools to <span className="text-secondary">Accelerate</span> Your Progress
          </h2>
          <p className="text-base sm:text-lg text-darkGray max-w-2xl mx-auto leading-relaxed">
            Explore our suite of practical tools designed to streamline and enhance your innovation efforts.
          </p>
        </div>

        {/* Improved tools grid with enhanced mobile layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {tools.map((tool, index) => {
            // Color variations for visual interest
            const colorVariants = [
              'from-primaryLight to-primaryLighter',
              'from-secondaryLight to-secondaryLighter',
              'from-primaryLighter to-secondaryLighter',
              'from-secondaryLighter to-primaryLighter',
              'from-primaryLight to-secondaryLight',
              'from-secondaryLight to-primaryLight'
            ];
            
            return (
              <article 
                key={index} 
                className="bg-white rounded-lg shadow-lg p-6 border border-lightGray hover:shadow-xl transition-all duration-300 flex flex-col h-full"
              >
                {/* Icon with gradient background */}
                <div className="mb-5">
                  <div 
                    className={`w-14 h-14 rounded-xl bg-gradient-to-br ${colorVariants[index]} flex items-center justify-center mb-4 shadow-md`} 
                    aria-hidden="true"
                  >
                    {React.createElement(tool.icon, { size: 24, className: 'text-black' })}
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-black mb-3">{tool.title}</h3>
                  <p className="text-sm sm:text-base text-darkGray flex-grow">{tool.description}</p>
                </div>
                
                {/* CTA button with improved accessibility */}
                <a 
                  href={tool.link} 
                  className={`inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium ${
                    index % 3 === 0 ? 'bg-primary text-white' : 
                    (index % 3 === 1 ? 'bg-secondary text-black' : 'border border-primary text-primary')
                  } hover:shadow-md transition-all duration-300 mt-auto self-start`}
                  aria-label={`${tool.cta} - ${tool.title}`}
                >
                  {tool.cta}
                  <ArrowUpRight size={16} className="ml-1.5" aria-hidden="true" />
                </a>
              </article>
            );
          })}
        </div>
        
        {/* Tool Suite Banner - Mobile optimized */}
        <div className="mt-12 md:mt-16 text-center">
          <div 
            className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-primaryLight to-secondaryLight px-5 py-2.5 shadow-md"
            itemScope
            itemType="https://schema.org/Thing"  
          >
            <Layers size={18} className="mr-2 text-black" aria-hidden="true" />
            <span className="font-semibold text-sm sm:text-base" itemProp="description">All tools integrate seamlessly with our methodology</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Optimized ArticleCover Component ---
const ArticleCover = React.memo(({ title, category, seed, imageQuality = 'medium' }) => {
  // Generate consistent colors based on seed (article title or ID)
  const getHashCode = useCallback((str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash);
  }, []);
  
  // Function to get a background color with more vibrant options
  const getBgColor = useCallback((str) => {
    const hash = getHashCode(str);
    // Use Lusidea palette combined colors to create gradients
    const bgOptions = [
      'bg-gradient-to-br from-primary/10 to-primary/30',
      'bg-gradient-to-br from-secondary/10 to-secondary/30',
      'bg-gradient-to-br from-primary/5 to-secondary/20',
      'bg-gradient-to-br from-secondary/5 to-primary/20',
      'bg-gradient-to-br from-primaryLight to-secondaryLight',
      'bg-gradient-to-br from-secondaryLight to-primaryLight'
    ];
    return bgOptions[hash % bgOptions.length];
  }, [getHashCode]);
  
  // Get pattern based on category - simplified for performance
  const getPattern = useCallback((category) => {
    if (!category) return null;
    
    const patterns = {
      'Innovation Strategy': {
        element: <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `radial-gradient(${COLORS.primary} 1px, transparent 1px)`,
          backgroundSize: '20px 20px'
        }} aria-hidden="true"></div>,
      },
      'Future Thinking': {
        element: <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `linear-gradient(135deg, ${COLORS.secondary} 25%, transparent 25%)`,
          backgroundSize: '20px 20px'
        }} aria-hidden="true"></div>,
      },
      'Design Thinking': {
        element: <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `radial-gradient(circle, ${COLORS.primary} 1px, transparent 1px)`,
          backgroundSize: '16px 16px'
        }} aria-hidden="true"></div>,
      }
    };
    
    return patterns[category]?.element || (
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: `radial-gradient(${COLORS.mediumGray} 1px, transparent 1px)`,
        backgroundSize: '20px 20px'
      }} aria-hidden="true"></div>
    );
  }, []);
  
  // Get initials for the icon
  const getInitials = useCallback((title) => {
    if (!title) return "L";
    const words = title.split(' ');
    if (words.length === 1) return title.substring(0, 2).toUpperCase();
    return (words[0][0] + (words[1]?.[0] || '')).toUpperCase();
  }, []);
  
  const bgClass = getBgColor(seed || title);
  const initials = getInitials(title);
  const pattern = getPattern(category);
  
  // Different styles based on image quality
  const sizeClasses = {
    small: 'w-10 h-10 text-base',
    medium: 'w-12 h-12 text-lg',
    large: 'w-16 h-16 text-xl'
  };
  
  return (
    <div 
      className={`w-full h-full flex items-center justify-center ${bgClass} relative overflow-hidden`} 
      role="img" 
      aria-label={`Cover for article: ${title}`}
    >
      {/* Pattern background */}
      {pattern}
      
      {/* Add category label */}
      <div className="absolute top-3 left-3 bg-white/90 px-2 py-1 rounded-md shadow-sm">
        <span className="text-xs font-semibold text-primary">{category}</span>
      </div>
      
      {/* Add article initials in a circle */}
      <div className="flex flex-col items-center">
        <div 
          className={`${sizeClasses[imageQuality]} rounded-full bg-white/30 flex items-center justify-center mb-3 shadow-md border border-white/40`} 
          aria-hidden="true"
        >
          <span className="font-bold text-black">{initials}</span>
        </div>
        
        {/* Title preview (shortened for cover) */}
        <div className="max-w-[80%] text-center px-3 py-2 bg-white/90 rounded-md shadow-md">
          <h4 className="text-xs font-bold text-black line-clamp-2">
            {title.length > 50 ? title.substring(0, 50) + '...' : title}
          </h4>
        </div>
      </div>
    </div>
  );
});

ArticleCover.displayName = 'ArticleCover';

// --- Optimized SimplifiedBlog Component ---
const SimplifiedBlog = React.memo(({ setToast }) => {
  // State for blog posts with proper loading and error handling
  const [posts, setPosts] = useState([]);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Set for bookmarked posts with localStorage persistence
  const [bookmarkedPosts, setBookmarkedPosts] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('lusidea-bookmarked-posts');
        return saved ? JSON.parse(saved) : [];
      } catch (e) {
        console.error('Error loading bookmarks from localStorage:', e);
        return [];
      }
    }
    return [];
  });
  
  // Fetch blog posts from API with improved error handling
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // In a real app, this would be a fetch call to your API
        const response = await fetch('/data/blog.json');
        
        if (!response.ok) {
          throw new Error(`Failed to fetch blog data: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Sort posts by published date, newest first
        const sortedPosts = data.sort((a, b) => 
          new Date(b['Published On']) - new Date(a['Published On'])
        );
        
        // Add view count and read time fields
        const enhancedPosts = sortedPosts.map(post => ({
          ...post,
          viewCount: Math.floor(Math.random() * 1500) + 200,
          readTime: calculateReadTime(post['Blog Post - Richt Text'])
        }));
        
        // Set all posts
        setPosts(enhancedPosts);
        
        // Set the featured post (first post or one marked as featured)
        const featured = enhancedPosts.find(post => post['Featured'] === 'Yes') || enhancedPosts[0];
        setFeaturedPost(featured);
        
        // Set recent posts (excluding featured)
        const recent = enhancedPosts
          .filter(post => post['Blog Post - Link'] !== featured['Blog Post - Link'])
          .slice(0, 3); // Only take 3 recent posts
        
        setRecentPosts(recent);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blog data:', err);
        setError(err.message);
        setLoading(false);
        
        if (setToast) {
          setToast({
            message: 'Unable to load blog posts. Please try again later.',
            type: 'error'
          });
        }
      }
    };
    
    fetchData();
  }, [setToast]);
  
  // Save bookmarks when they change with error handling
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('lusidea-bookmarked-posts', JSON.stringify(bookmarkedPosts));
      } catch (e) {
        console.error('Error saving bookmarks to localStorage:', e);
        if (setToast) {
          setToast({
            message: 'Unable to save bookmark preferences. Please check your browser settings.',
            type: 'error'
          });
        }
      }
    }
  }, [bookmarkedPosts, setToast]);
  
  // Toggle bookmark for a post
  const toggleBookmark = useCallback((postLink) => {
    setBookmarkedPosts(prev => {
      if (prev.includes(postLink)) {
        setToast({
          message: 'Bookmark removed',
          type: 'info'
        });
        return prev.filter(link => link !== postLink);
      } else {
        setToast({
          message: 'Article bookmarked successfully',
          type: 'success'
        });
        return [...prev, postLink];
      }
    });
  }, [setToast]);
  
  // Share article with improved Web Share API and error handling
  const shareArticle = useCallback((post) => {
    const postUrl = `/blog/${post['Blog Post - Link']}`;
    if (navigator.share) {
      navigator.share({
        title: post['Blog Post - Title'],
        text: post['Blog Post - Short Description'],
        url: postUrl
      })
      .then(() => {
        setToast({
          message: 'Article shared successfully',
          type: 'success'
        });
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          console.log('Error sharing', error);
          copyToClipboard(postUrl);
        }
      });
    } else {
      // Fallback to clipboard copy
      copyToClipboard(postUrl);
    }
  }, [setToast]);
  
  // Helper to copy to clipboard with feedback and error handling
  const copyToClipboard = useCallback((text) => {
    try {
      const absoluteUrl = new URL(text, window.location.origin).toString();
      navigator.clipboard.writeText(absoluteUrl)
        .then(() => {
          setToast({
            message: 'Link copied to clipboard',
            type: 'success'
          });
        })
        .catch(err => {
          console.error('Failed to copy:', err);
          setToast({
            message: 'Failed to copy link. Please try again.',
            type: 'error'
          });
        });
    } catch (e) {
      console.error('Error creating URL:', e);
      setToast({
        message: 'Failed to copy link. Please try again.',
        type: 'error'
      });
    }
  }, [setToast]);
  
  // Calculate read time based on content length with improved algorithm
  const calculateReadTime = useCallback((content) => {
    if (!content) return 3; // Default read time if no content available
    
    // Strip HTML tags and count words
    const text = content.replace(/<[^>]*>/g, '');
    const wordCount = text.split(/\s+/).length;
    
    // Average reading speed: 200-250 words per minute
    // Additional time for images, code blocks, etc.
    const minutes = Math.ceil(wordCount / 225);
    return Math.max(minutes, 1); // Minimum 1 minute read time
  }, []);
  
  // Format date nicely with localization
  const formatDate = useCallback((dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateString; // Fallback to original string
    }
  }, []);

  return (
    <section id="blog" className="py-16 md:py-20 lg:py-24 bg-offWhite">
      <div className={SPACING.container}>
        {/* Section header with improved semantics */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-block rounded-full bg-primaryLight px-4 py-1.5 mb-3">
            <p className="text-sm font-semibold text-primary uppercase tracking-wide">
              Knowledge Center
            </p>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-4">
            Innovation <span className="text-primary">Insights</span> & Ideas
          </h2>
          <p className="text-base sm:text-lg text-darkGray max-w-2xl mx-auto leading-relaxed">
            Explore our latest thinking on innovation strategies, methodologies, and success stories across industries.
          </p>
        </div>

        {/* Error state with improved recovery options */}
        {error && (
          <div className="text-center py-12 bg-white rounded-xl shadow-md">
            <div className="inline-flex items-center justify-center rounded-full w-14 h-14 bg-primaryLight mb-4">
              <X size={24} className="text-primary" aria-hidden="true" />
            </div>
            <h3 className="text-xl font-bold mb-3">Unable to Load Blog Posts</h3>
            <p className="text-mediumGray max-w-lg mx-auto mb-6">
              We're having trouble retrieving our latest articles. Please try again later or check your internet connection.
            </p>
            <Button 
              variant="primary" 
              size="md" 
              onClick={() => window.location.reload()}
              aria-label="Reload page to try again"
            >
              Retry
            </Button>
          </div>
        )}

        {/* Loading state for blog posts */}
        {loading && !error ? (
          <div 
            className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-2" 
            aria-busy="true" 
            aria-label="Loading blog posts"
          >
            {/* Featured post skeleton */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-lightGray/30 md:col-span-2">
              <div className="grid md:grid-cols-12 gap-0">
                <div className="md:col-span-5 h-64 bg-lightGray/40 animate-pulse"></div>
                <div className="md:col-span-7 p-6">
                  <div className="h-6 w-28 bg-lightGray/40 rounded-full mb-3 animate-pulse"></div>
                  <div className="h-7 w-full bg-lightGray/40 rounded-md mb-2 animate-pulse"></div>
                  <div className="h-7 w-4/5 bg-lightGray/40 rounded-md mb-3 animate-pulse"></div>
                  <div className="h-4 w-full bg-lightGray/40 rounded-md mb-2 animate-pulse"></div>
                  <div className="h-4 w-full bg-lightGray/40 rounded-md mb-2 animate-pulse"></div>
                  <div className="h-4 w-4/5 bg-lightGray/40 rounded-md mb-4 animate-pulse"></div>
                  <div className="h-8 w-36 bg-lightGray/40 rounded-md mt-3 animate-pulse"></div>
                </div>
              </div>
            </div>
            
            {/* Recent posts skeletons */}
            {[...Array(3)].map((_, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden border border-lightGray/30 flex flex-col h-full">
                <div className="h-40 bg-lightGray/40 animate-pulse"></div>
                <div className="p-5 flex flex-col flex-grow">
                  <div className="h-5 w-20 bg-lightGray/40 rounded-full mb-2 animate-pulse"></div>
                  <div className="h-6 w-full bg-lightGray/40 rounded-md mb-2 animate-pulse"></div>
                  <div className="h-6 w-4/5 bg-lightGray/40 rounded-md mb-3 animate-pulse"></div>
                  <div className="h-4 w-full bg-lightGray/40 rounded-md mb-2 animate-pulse"></div>
                  <div className="h-4 w-3/4 bg-lightGray/40 rounded-md mb-3 animate-pulse"></div>
                  <div className="h-7 w-28 bg-lightGray/40 rounded-md mt-auto animate-pulse"></div>
                </div>
              </div>
            ))}
          </div>
        ) : !error ? (
          <div className="space-y-10">
            {/* Featured Post - Mobile optimized */}
            {featuredPost && (
              <div className="mb-10">
                <h3 className="text-xl font-bold mb-5 flex items-center">
                  <Star size={22} className="text-secondary mr-2" aria-hidden="true" />
                  Featured Article
                </h3>
                
                <article className="bg-white rounded-xl overflow-hidden shadow-lg border border-lightGray/30 hover:shadow-xl transition-all duration-300">
                  <div className="grid md:grid-cols-12 gap-0">
                    {/* Image */}
                    <div className="md:col-span-5 h-64 md:h-auto relative overflow-hidden">
                      {/* Featured tag */}
                      <div className="absolute top-4 left-0 bg-primary z-10 py-1.5 pl-5 pr-3 rounded-r-full shadow-md">
                        <div className="flex items-center">
                          <Star size={14} className="text-white mr-1.5" fill="white" aria-hidden="true" />
                          <span className="text-white text-xs font-semibold">Featured</span>
                        </div>
                      </div>
                      
                      {/* Article cover */}
                      <a 
                        href={`/blog/${featuredPost['Blog Post - Link']}`} 
                        className="block h-full" 
                        aria-label={featuredPost['Blog Post - Title']}
                      >
                        <ArticleCover 
                          title={featuredPost['Blog Post - Title']} 
                          category={featuredPost['New Category']} 
                          seed={featuredPost['Blog Post - Link']} 
                          imageQuality="large"
                        />
                      </a>
                    </div>
                    
                    {/* Content */}
                    <div className="md:col-span-7 p-6">
                      <div className="flex flex-wrap items-center gap-2 mb-4">
                        {/* Category badge */}
                        <a 
                          href={`/blog?category=${encodeURIComponent(featuredPost['New Category'])}`} 
                          className="inline-block bg-primaryLight px-3 py-1 rounded-full text-primary text-xs font-semibold hover:bg-primary hover:text-white transition-colors duration-300"
                        >
                          {featuredPost['New Category']}
                        </a>
                        
                        {/* Date badge */}
                        <span className="text-xs text-mediumGray flex items-center">
                          <Calendar size={12} className="mr-1" aria-hidden="true" />
                          <time dateTime={featuredPost['Published On']}>{formatDate(featuredPost['Published On'])}</time>
                        </span>
                      </div>
                      
                      {/* Title */}
                      <a 
                        href={`/blog/${featuredPost['Blog Post - Link']}`} 
                        className="group hover:text-primary transition-colors duration-300"
                      >
                        <h4 className="text-xl md:text-2xl font-bold text-black mb-3 group-hover:text-primary transition-colors">
                          {featuredPost['Blog Post - Title']}
                        </h4>
                      </a>
                      
                      {/* Description */}
                      <p className="text-darkGray mb-5 line-clamp-3 text-sm sm:text-base leading-relaxed">
                        {featuredPost['Blog Post - Short Description']}
                      </p>
                      
                      <div className="flex items-center justify-between gap-3 mt-auto pt-4 border-t border-lightGray/30">
                        {/* Read more link */}
                        <a 
                          href={`/blog/${featuredPost['Blog Post - Link']}`}
                          className="inline-flex items-center text-primary font-medium hover:underline text-sm"
                          aria-label={`Read full article: ${featuredPost['Blog Post - Title']}`}
                        >
                          Read Full Article 
                          <ArrowRight size={14} className="ml-1.5" aria-hidden="true" />
                        </a>
                        
                        {/* Read time */}
                        <div className="flex items-center text-xs text-mediumGray">
                          <Clock size={12} className="mr-1" aria-hidden="true" />
                          {featuredPost.readTime} min read
                        </div>
                      </div>
                      
                      {/* Action buttons */}
                      <div className="flex items-center gap-2 mt-4">
                        <button
                          onClick={() => toggleBookmark(featuredPost['Blog Post - Link'])}
                          className={`p-1.5 rounded-full ${
                            bookmarkedPosts.includes(featuredPost['Blog Post - Link'])
                              ? 'bg-secondaryLight text-secondary'
                              : 'bg-offWhite text-mediumGray hover:bg-primaryLight hover:text-primary'
                          } transition-colors`}
                          aria-label={bookmarkedPosts.includes(featuredPost['Blog Post - Link']) ? "Remove bookmark" : "Bookmark article"}
                        >
                          <Bookmark 
                            size={16} 
                            fill={bookmarkedPosts.includes(featuredPost['Blog Post - Link']) ? COLORS.secondary : "none"} 
                            aria-hidden="true"
                          />
                        </button>
                        <button
                          onClick={() => shareArticle(featuredPost)}
                          className="p-1.5 rounded-full bg-offWhite text-mediumGray hover:bg-primaryLight hover:text-primary transition-colors"
                          aria-label="Share article"
                        >
                          <Share2 size={16} aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            )}

            {/* Recent Posts - Improved for mobile */}
            <div>
              <h3 className="text-xl font-bold mb-5">Recent Articles</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {recentPosts.map((post, index) => (
                  <article
                    key={post['Blog Post - Link']}
                    className="bg-white rounded-xl shadow-md overflow-hidden border border-lightGray/30 hover:shadow-lg transition-all duration-300 flex flex-col h-full"
                  >
                    {/* Cover Image */}
                    <a 
                      href={`/blog/${post['Blog Post - Link']}`} 
                      className="block h-40 relative overflow-hidden" 
                      aria-label={post['Blog Post - Title']}
                    >
                      <ArticleCover 
                        title={post['Blog Post - Title']} 
                        category={post['New Category']} 
                        seed={post['Blog Post - Link']} 
                      />
                      
                      {/* Bookmark indicator */}
                      {bookmarkedPosts.includes(post['Blog Post - Link']) && (
                        <div 
                          className="absolute top-2 right-2 bg-secondary text-black rounded-full p-1 shadow-md" 
                          aria-label="Bookmarked article"
                        >
                          <Bookmark size={12} fill={COLORS.black} aria-hidden="true" />
                        </div>
                      )}
                    </a>
                    
                    <div className="p-4 flex flex-col flex-grow">
                      <div className="flex items-center justify-between mb-2">
                        {/* Category */}
                        <a 
                          href={`/blog?category=${encodeURIComponent(post['New Category'])}`}
                          className="text-xs font-semibold text-primary bg-primaryLighter px-2 py-1 rounded-full"
                        >
                          {post['New Category']}
                        </a>
                        <span className="text-xs text-mediumGray flex items-center">
                          <Clock size={10} className="mr-1" aria-hidden="true" />
                          {post.readTime} min
                        </span>
                      </div>
                      
                      {/* Title */}
                      <a 
                        href={`/blog/${post['Blog Post - Link']}`} 
                        className="group" 
                      >
                        <h4 
                          className="text-base font-bold text-black mb-2 group-hover:text-primary transition-colors line-clamp-2" 
                        >
                          {post['Blog Post - Title']}
                        </h4>
                      </a>
                      
                      {/* Description */}
                      <p 
                        className="text-xs text-darkGray line-clamp-3 mb-4 flex-grow" 
                      >
                        {post['Blog Post - Short Description']}
                      </p>
                      
                      <div className="flex justify-between items-center mt-auto">
                        <a 
                          href={`/blog/${post['Blog Post - Link']}`}
                          className="inline-flex items-center text-xs font-medium text-primary hover:underline"
                          aria-label={`Read article: ${post['Blog Post - Title']}`}
                        >
                          Read Article <ArrowRight size={12} className="ml-1" aria-hidden="true" />
                        </a>
                        
                        {/* Bookmark button */}
                        <button
                          onClick={() => toggleBookmark(post['Blog Post - Link'])}
                          className={`p-1 rounded-full ${
                            bookmarkedPosts.includes(post['Blog Post - Link'])
                              ? 'bg-secondaryLight text-secondary'
                              : 'text-mediumGray hover:text-primary'
                          } transition-colors`}
                          aria-label={bookmarkedPosts.includes(post['Blog Post - Link']) ? "Remove bookmark" : "Bookmark article"}
                        >
                          <Bookmark 
                            size={12} 
                            fill={bookmarkedPosts.includes(post['Blog Post - Link']) ? COLORS.secondary : "none"} 
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
            
            {/* View All Articles Button */}
            <div className="text-center mt-8">
              <a
                href="/blog"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-base font-semibold rounded-lg bg-transparent border-2 border-primary text-primary hover:bg-primaryLight transition-all duration-300 hover:shadow-md"
                aria-label="View all blog articles"
              >
                View All Articles
                <ArrowRight size={16} aria-hidden="true" />
              </a>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
});

SimplifiedBlog.displayName = 'SimplifiedBlog';

export default LusideaWebsite;